import React, { useState } from "react";
import { motion } from "framer-motion";
import { X } from "lucide-react";

interface OrderRejectCancelModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (reason: string, notes: string, visibleToReseller: boolean) => void;
  actionType: "reject" | "cancel";
  orderId: string;
}

const OrderRejectCancelModal: React.FC<OrderRejectCancelModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  actionType,
  orderId,
}) => {
  const [reason, setReason] = useState("");
  const [notes, setNotes] = useState("");
  const [visibleToReseller, setVisibleToReseller] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!reason.trim()) {
      return;
    }
    setIsSubmitting(true);
    try {
      await onSubmit(reason, notes, visibleToReseller);
      setReason("");
      setNotes("");
      setVisibleToReseller(false);
      onClose();
    } catch (error) {
      console.error(`Error ${actionType}ing order:`, error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setReason("");
    setNotes("");
    setVisibleToReseller(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="modal is-active" style={{ zIndex: 9999 }}>
      <div className="modal-background" onClick={handleClose}></div>
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        className="modal-card"
        style={{ maxWidth: "600px" }}
      >
        <header className="modal-card-head">
          <p className="modal-card-title">
            {actionType === "reject" ? "Rejeitar" : "Cancelar"} Encomenda #
            {orderId}
          </p>
          <button
            className="delete"
            aria-label="close"
            onClick={handleClose}
          ></button>
        </header>
        <section className="modal-card-body">
          <form onSubmit={handleSubmit}>
            <div className="field">
              <label className="label">
                Motivo <span className="has-text-danger">*</span>
              </label>
              <div className="control">
                <input
                  className="input"
                  type="text"
                  placeholder={`Motivo para ${actionType === "reject" ? "rejeitar" : "cancelar"} a encomenda`}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  required
                  disabled={isSubmitting}
                />
              </div>
              <p className="help">Campo obrigatório</p>
            </div>

            <div className="field">
              <label className="label">Notas Adicionais</label>
              <div className="control">
                <textarea
                  className="textarea"
                  placeholder="Informações adicionais (opcional)"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={4}
                  disabled={isSubmitting}
                />
              </div>
            </div>

            <div className="field">
              <div className="control">
                <label className="checkbox">
                  <input
                    type="checkbox"
                    checked={visibleToReseller}
                    onChange={(e) => setVisibleToReseller(e.target.checked)}
                    disabled={isSubmitting}
                  />
                  <span className="ml-2">Visível para o revendedor</span>
                </label>
              </div>
              <p className="help">
                Se marcado, o revendedor poderá ver o motivo e as notas
              </p>
            </div>
          </form>
        </section>
        <footer className="modal-card-foot">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className={`button ${actionType === "reject" ? "is-danger" : "is-warning"}`}
            onClick={handleSubmit}
            disabled={!reason.trim() || isSubmitting}
          >
            {isSubmitting
              ? "A processar..."
              : actionType === "reject"
                ? "Rejeitar Encomenda"
                : "Cancelar Encomenda"}
          </motion.button>
          <button
            className="button"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            Fechar
          </button>
        </footer>
      </motion.div>
    </div>
  );
};

export default OrderRejectCancelModal;
